import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Users, Send, User, ThumbsUp, MessageCircle, Share2 } from 'lucide-react';
interface Post {
  id: string;
  author: {
    name: string;
    avatar?: string;
  };
  content: string;
  timestamp: string;
  likes: number;
  comments: number;
  liked: boolean;
}
const mockPosts: Post[] = [{
  id: '1',
  author: {
    name: 'Sarah Johnson',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg'
  },
  content: 'Just completed the Systems Thinking course and it was amazing! The frameworks really helped me understand complex problems better. Has anyone applied these concepts to a real project yet?',
  timestamp: '2 hours ago',
  likes: 24,
  comments: 5,
  liked: false
}, {
  id: '2',
  author: {
    name: 'Michael Okafor',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
  },
  content: "I'm struggling with the Project Management module. Any tips on creating a good Gantt chart for university projects would appreciate any help.",
  timestamp: '5 hours ago',
  likes: 7,
  comments: 12,
  liked: false
}, {
  id: '3',
  author: {
    name: 'Amina Diallo',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg'
  },
  content: 'The interview preparation course helped me land an internship at a tech company! The mock interviews were particularly helpful. Happy to share my experience if anyone has questions.',
  timestamp: '1 day ago',
  likes: 45,
  comments: 8,
  liked: true
}];
const Community: React.FC = () => {
  const {
    user
  } = useAuth();
  const [posts, setPosts] = useState<Post[]>(mockPosts);
  const [newPost, setNewPost] = useState('');
  const [activeTab, setActiveTab] = useState('discussions');
  const handlePostSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    const post: Post = {
      id: Date.now().toString(),
      author: {
        name: user?.name || 'Anonymous'
      },
      content: newPost,
      timestamp: 'Just now',
      likes: 0,
      comments: 0,
      liked: false
    };
    setPosts([post, ...posts]);
    setNewPost('');
  };
  const toggleLike = (postId: string) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        const wasLiked = post.liked;
        return {
          ...post,
          liked: !wasLiked,
          likes: wasLiked ? post.likes - 1 : post.likes + 1
        };
      }
      return post;
    }));
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-404">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-405">
        <div className="text-center mb-12" data-id="element-406">
          <h1 className="text-3xl font-bold text-gray-800 mb-4" data-id="element-407">Community</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-408">
            Connect with other students, share insights, ask questions, and grow
            together.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-409">
          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-410">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-411">
              <div className="flex items-center space-x-3 mb-6" data-id="element-412">
                <div className="bg-blue-100 rounded-full p-3" data-id="element-413">
                  <User size={20} className="text-blue-600" data-id="element-414" />
                </div>
                <div data-id="element-415">
                  <h3 className="font-medium text-gray-800" data-id="element-416">{user?.name}</h3>
                  <p className="text-sm text-gray-500" data-id="element-417">{user?.university}</p>
                </div>
              </div>
              <div className="border-t pt-4" data-id="element-418">
                <p className="text-sm text-gray-600 mb-2" data-id="element-419">Your Activity</p>
                <div className="flex justify-between text-sm text-gray-500" data-id="element-420">
                  <span data-id="element-421">Posts: 3</span>
                  <span data-id="element-422">Comments: 12</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-423">
              <h3 className="font-medium text-gray-800 mb-4" data-id="element-424">
                Community Links
              </h3>
              <nav className="space-y-2" data-id="element-425">
                <a href="#" className="flex items-center px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-blue-50 hover:text-blue-700" data-id="element-426">
                  <Users size={16} className="mr-3" data-id="element-427" />
                  Study Groups
                </a>
                <a href="#" className="flex items-center px-3 py-2 text-sm rounded-md text-gray-700 hover:bg-blue-50 hover:text-blue-700" data-id="element-428">
                  <MessageSquare size={16} className="mr-3" data-id="element-429" />
                  WhatsApp Group
                </a>
              </nav>
            </div>
          </div>
          {/* Main Content */}
          <div className="lg:col-span-3" data-id="element-430">
            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm mb-6" data-id="element-431">
              <div className="flex border-b" data-id="element-432">
                <button onClick={() => setActiveTab('discussions')} className={`flex-1 py-4 px-6 text-center font-medium ${activeTab === 'discussions' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-433">
                  <MessageSquare size={18} className="inline mr-2" data-id="element-434" />
                  Discussions
                </button>
                <button onClick={() => setActiveTab('study-groups')} className={`flex-1 py-4 px-6 text-center font-medium ${activeTab === 'study-groups' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-435">
                  <Users size={18} className="inline mr-2" data-id="element-436" />
                  Study Groups
                </button>
              </div>
            </div>
            {activeTab === 'discussions' && <>
                {/* Post creation */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-437">
                  <form onSubmit={handlePostSubmit} data-id="element-438">
                    <div className="mb-4" data-id="element-439">
                      <textarea placeholder="Share something with the community..." className="w-full border border-gray-300 rounded-md p-3 focus:ring-blue-500 focus:border-blue-500" rows={3} value={newPost} onChange={e => setNewPost(e.target.value)} data-id="element-440"></textarea>
                    </div>
                    <div className="flex justify-end" data-id="element-441">
                      <button type="submit" disabled={!newPost.trim()} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300" data-id="element-442">
                        <Send size={16} className="mr-2" data-id="element-443" />
                        Post
                      </button>
                    </div>
                  </form>
                </div>
                {/* Posts */}
                <div className="space-y-6" data-id="element-444">
                  {posts.map(post => <div key={post.id} className="bg-white rounded-lg shadow-sm p-6" data-id="element-445">
                      <div className="flex items-start space-x-3 mb-4" data-id="element-446">
                        {post.author.avatar ? <img src={post.author.avatar} alt={post.author.name} className="w-10 h-10 rounded-full" data-id="element-447" /> : <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-448">
                            <span className="text-blue-600 font-medium" data-id="element-449">
                              {post.author.name.charAt(0)}
                            </span>
                          </div>}
                        <div data-id="element-450">
                          <h4 className="font-medium text-gray-800" data-id="element-451">
                            {post.author.name}
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-452">
                            {post.timestamp}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4" data-id="element-453">{post.content}</p>
                      <div className="flex space-x-6 text-sm" data-id="element-454">
                        <button onClick={() => toggleLike(post.id)} className={`flex items-center ${post.liked ? 'text-blue-600' : 'text-gray-500 hover:text-blue-600'}`} data-id="element-455">
                          <ThumbsUp size={16} className="mr-1" data-id="element-456" />
                          <span data-id="element-457">{post.likes}</span>
                        </button>
                        <button className="flex items-center text-gray-500 hover:text-blue-600" data-id="element-458">
                          <MessageCircle size={16} className="mr-1" data-id="element-459" />
                          <span data-id="element-460">{post.comments}</span>
                        </button>
                        <button className="flex items-center text-gray-500 hover:text-blue-600" data-id="element-461">
                          <Share2 size={16} className="mr-1" data-id="element-462" />
                          <span data-id="element-463">Share</span>
                        </button>
                      </div>
                    </div>)}
                </div>
              </>}
            {activeTab === 'study-groups' && <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-464">
                <h2 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-465">
                  Study Groups
                </h2>
                <div className="space-y-4" data-id="element-466">
                  <div className="border rounded-lg p-4 hover:bg-blue-50 transition-colors" data-id="element-467">
                    <div className="flex justify-between items-start" data-id="element-468">
                      <div data-id="element-469">
                        <h3 className="font-medium text-gray-800" data-id="element-470">
                          Project Management Study Group
                        </h3>
                        <p className="text-sm text-gray-600 mb-2" data-id="element-471">
                          Weekly discussions on project management best
                          practices
                        </p>
                        <div className="flex items-center text-xs text-gray-500" data-id="element-472">
                          <Users size={14} className="mr-1" data-id="element-473" />
                          <span data-id="element-474">24 members</span>
                        </div>
                      </div>
                      <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700" data-id="element-475">
                        Join
                      </button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4 hover:bg-blue-50 transition-colors" data-id="element-476">
                    <div className="flex justify-between items-start" data-id="element-477">
                      <div data-id="element-478">
                        <h3 className="font-medium text-gray-800" data-id="element-479">
                          Interview Prep Group
                        </h3>
                        <p className="text-sm text-gray-600 mb-2" data-id="element-480">
                          Practice interviews and feedback sessions
                        </p>
                        <div className="flex items-center text-xs text-gray-500" data-id="element-481">
                          <Users size={14} className="mr-1" data-id="element-482" />
                          <span data-id="element-483">18 members</span>
                        </div>
                      </div>
                      <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700" data-id="element-484">
                        Join
                      </button>
                    </div>
                  </div>
                  <div className="border rounded-lg p-4 hover:bg-blue-50 transition-colors" data-id="element-485">
                    <div className="flex justify-between items-start" data-id="element-486">
                      <div data-id="element-487">
                        <h3 className="font-medium text-gray-800" data-id="element-488">
                          Systems Thinking Discussion
                        </h3>
                        <p className="text-sm text-gray-600 mb-2" data-id="element-489">
                          Deep dive into systems thinking applications
                        </p>
                        <div className="flex items-center text-xs text-gray-500" data-id="element-490">
                          <Users size={14} className="mr-1" data-id="element-491" />
                          <span data-id="element-492">12 members</span>
                        </div>
                      </div>
                      <button className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700" data-id="element-493">
                        Joined
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-8 text-center" data-id="element-494">
                  <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50" data-id="element-495">
                    Create New Study Group
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default Community;